import { Box, Flex } from '@chakra-ui/react';
import { Form, Formik } from 'formik';
import { type ReactNode, useMemo } from 'react';
import * as Yup from 'yup';

import { Input, Select } from '../../../components/formComponents';
import DateInput from '../../../components/formComponents/DateInput';
import SSNInput from '../../../components/formComponents/ssn/SSNInput';
import { statesArray } from '../../../constants/states';
import { LienholderRequirement } from '../../../gql/generated/graphql';
import {
  ssnFullValidation,
  ssnLast4Validation,
  ssnLast6Validation,
} from '../../../utils/validation/SSNumber';
import { dobValidation } from '../../../utils/validation/dob';
import { zipValidation } from '../../../utils/validation/zipCodes';

export type UncollectedLienholderRequirements = Exclude<
  LienholderRequirement,
  | LienholderRequirement.Make
  | LienholderRequirement.Mileage
  | LienholderRequirement.Model
  | LienholderRequirement.Vin
  | LienholderRequirement.Year
>;

export const lienholderRequirementFieldFormMapping: Record<
  UncollectedLienholderRequirements,
  {
    fieldName: string;
    initialValue: string | number | boolean | Date | null | undefined;
    validationSchema: Yup.AnySchema;
    component: ReactNode;
  }
> = {
  [LienholderRequirement.AddressLine]: {
    fieldName: 'address_line',
    initialValue: '',
    validationSchema: Yup.string().required('Please enter your address'),
    component: <Input label="ADDRESS" name="address_line" placeholder="Street address" />,
  },
  [LienholderRequirement.City]: {
    fieldName: 'city',
    initialValue: '',
    validationSchema: Yup.string().required('Please enter your city'),
    component: <Input label="CITY" name="city" placeholder="City" />,
  },
  [LienholderRequirement.Dob]: {
    fieldName: 'dob',
    initialValue: undefined,
    validationSchema: dobValidation,
    component: <DateInput hideCalendar label="DATE OF BIRTH" name="dob" />,
  },
  [LienholderRequirement.DobYear]: {
    fieldName: 'dob',
    initialValue: undefined,
    validationSchema: dobValidation,
    component: (
      <DateInput
        _container={{ mr: { base: 0, md: 2 }, mb: { base: 4, md: 0 } }}
        hideCalendar
        label="BIRTHDATE"
        name="dob"
      />
    ),
  },
  [LienholderRequirement.FirstName]: {
    fieldName: 'first_name',
    initialValue: '',
    validationSchema: Yup.string().notRequired(),
    component: <Input label="FIRST NAME" name="first_name" placeholder="First" />,
  },
  [LienholderRequirement.LastFourSsn]: {
    fieldName: 'ssn',
    initialValue: '',
    validationSchema: ssnLast4Validation,
    component: <SSNInput digits="4" name="ssn" label="LAST 4 OF SSN" />,
  },
  [LienholderRequirement.LastName]: {
    fieldName: 'last_name',
    initialValue: '',
    validationSchema: Yup.string().notRequired(),
    component: <Input label="LAST NAME" name="last_name" placeholder="Last" />,
  },
  [LienholderRequirement.LastSixSsn]: {
    fieldName: 'ssn',
    initialValue: '',
    validationSchema: ssnLast6Validation,
    component: <SSNInput digits="6" name="ssn" label="LAST 6 OF SSN" />,
  },
  [LienholderRequirement.PhoneNumber1]: {
    fieldName: 'phone_number',
    initialValue: '',
    validationSchema: Yup.string().notRequired(),
    component: (
      <Input
        label="Phone Number"
        name="phone_number"
        mask="(999) 999-9999"
        _container={{ mb: 2 }}
        placeholder="Phone Number"
        type="tel"
      />
    ),
  },
  [LienholderRequirement.Ssn]: {
    fieldName: 'ssn',
    initialValue: '',
    validationSchema: ssnFullValidation,
    component: <SSNInput digits="9" name="ssn" label="SOCIAL SECURITY NUMBER" />,
  },
  [LienholderRequirement.State]: {
    fieldName: 'state',
    initialValue: '',
    validationSchema: Yup.string().required('Please select a state'),
    component: (
      <Select label="STATE" name="state" options={statesArray} placeholder="Search State" />
    ),
  },
  [LienholderRequirement.Zip]: {
    fieldName: 'zip',
    initialValue: '',
    validationSchema: zipValidation,
    component: <Input label="ZIP" name="zip" placeholder="ZIP" />,
  },
};

export type PayoffMissingFieldFormValues = Record<
  string,
  string | number | boolean | Date | null | undefined
>;

export type PayoffMissingFieldsSubmitState = {
  isSubmitting: boolean;
  isValid: boolean;
};

type PayoffIntelligenceMissingFieldsProps = {
  missingFields: LienholderRequirement[];
  onSubmit: (values: PayoffMissingFieldFormValues) => Promise<void> | void;
  submitButton: (state: PayoffMissingFieldsSubmitState) => ReactNode;
  validateOnBlur?: boolean;
  validateOnChange?: boolean;
  fieldsLayout?: 'wrap' | 'stack';
  belowFields?: ReactNode;
};

const PayoffIntelligenceMissingFields = ({
  missingFields,
  onSubmit,
  submitButton,
  validateOnBlur = false,
  validateOnChange = false,
  fieldsLayout = 'wrap',
  belowFields,
}: PayoffIntelligenceMissingFieldsProps) => {
  const yupObjectFields = useMemo(
    () =>
      missingFields.reduce((acc, field) => {
        const collected = field as UncollectedLienholderRequirements;
        const mapping = lienholderRequirementFieldFormMapping[collected];
        if (mapping) {
          acc[mapping.fieldName] = mapping.validationSchema;
        }
        return acc;
      }, {} as Record<string, Yup.AnySchema>),
    [missingFields],
  );
  const validationSchema = Yup.object(yupObjectFields);

  const initialValues = useMemo(
    () =>
      missingFields.reduce((acc, field) => {
        const collected = field as UncollectedLienholderRequirements;
        const mapping = lienholderRequirementFieldFormMapping[collected];
        if (mapping) {
          acc[mapping.fieldName] = mapping.initialValue;
        }
        return acc;
      }, {} as PayoffMissingFieldFormValues),
    [missingFields],
  );

  return (
    <Box>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={validationSchema}
        validateOnBlur={validateOnBlur}
        validateOnChange={validateOnChange}
        onSubmit={onSubmit}
      >
        {({ isSubmitting, isValid }) => (
          <Form>
            <Flex
              flexWrap={fieldsLayout === 'wrap' ? 'wrap' : 'nowrap'}
              flexDir={fieldsLayout === 'stack' ? 'column' : 'row'}
              alignItems={fieldsLayout === 'wrap' ? 'flex-end' : 'stretch'}
              gap={4}
              mt={4}
            >
              {missingFields.map((field) => {
                const component =
                  lienholderRequirementFieldFormMapping?.[
                    field as UncollectedLienholderRequirements
                  ]?.component;
                if (!component) {
                  return null;
                }
                return (
                  <Box key={field} flex="1 1 200px" minW="180px">
                    {component}
                  </Box>
                );
              })}
            </Flex>
            {belowFields}
            <Flex flexDir="column" alignItems="center" mt={belowFields ? 4 : '30px'}>
              {submitButton({ isSubmitting, isValid })}
            </Flex>
          </Form>
        )}
      </Formik>
    </Box>
  );
};

export default PayoffIntelligenceMissingFields;
